// components/JourneyView.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import "./journey.css";
import { Base_Url } from '../config';

const DynamicIcon = ({ name, size = 24, color, className }) => {
  if (!name) {
    console.warn('No icon name provided to DynamicIcon component');
    return null;
  }
 
  try {
    // Clean the icon name whether it's in JSX format or plain text
    let cleanIconName = name;
         
    // If it's in JSX format (e.g., <Computer />)
    if (name.startsWith('<') && name.endsWith('>')) {
      cleanIconName = name.replace(/[<>/]/g, '').trim();
    }
    // If it's just the name (e.g., Computer)
    else {
      cleanIconName = name.trim();
    }
 
    // Get the icon component from Lucide icons
    const IconComponent = LucideIcons[cleanIconName];
 
    if (!IconComponent) {
      console.warn(`Icon "${cleanIconName}" not found in Lucide icons`);
      return <LucideIcons.Computer size={size} color={color} className={className} />;
    }
 
    return <IconComponent size={size} color={color} className={className} />;
  } catch (error) {
    console.error('Error rendering Lucide icon:', error);
    return <LucideIcons.AlertCircle color="red" size={size} className={className} />;
  }
};

const JourneyView = () => {
  const [activeSection, setActiveSection] = useState('faculty');
  const [facultyData, setFacultyData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(true);

  // API functions - matching AdminPanel endpoints
  const createAPI = (basePath) => ({
    getSections: () => fetch(`${Base_Url}/${basePath}-sections`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
  });

  const facultyJourneyAPI = createAPI('faculty');
  const studentJourneyAPI = createAPI('student');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // console.log('Fetching journey data...');
      
      const [facultyRes, studentRes] = await Promise.all([
        facultyJourneyAPI.getSections().catch(err => {
          console.error('Error fetching faculty data:', err);
          return [];
        }),
        studentJourneyAPI.getSections().catch(err => {
          console.error('Error fetching student data:', err);
          return [];
        })
      ]);
      
      // console.log('Faculty Response:', facultyRes);
      // console.log('Student Response:', studentRes);
      
      // Ensure each section has a subsections array
      const formattedFacultyData = facultyRes.map(section => ({
        ...section,
        subsections: section.subsections || []
      }));
      
      const formattedStudentData = studentRes.map(section => ({
        ...section,
        subsections: section.subsections || []
      }));
      
      setFacultyData(formattedFacultyData);
      setStudentData(formattedStudentData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setFacultyData([]);
      setStudentData([]);
    } finally {
      setLoading(false);
    }
  };

  const renderMenuItems = (sections) => {
    if (!sections || sections.length === 0) {
      return <div className="text-center py-4 text-muted">No data available</div>;
    }

    return sections.map(section => (
      <div className="menu-item" key={section.id}>
        <span className="menu-icon ">
          {section.icon_class ? (
            <DynamicIcon  name={section.icon_class} size={20} />
          ) : (
            <LucideIcons.Circle size={20} />
          )}
        </span>
        {section.title}
        {section.subsections && section.subsections.length > 0 && (
          <ul className="submenu">
            {section.subsections.map(sub => (
              <li key={sub.id}>
                {sub.link ? (
                  sub.link.startsWith('/') ? (
                    <Link to={sub.link} className="external-link">
                      {sub.title} <LucideIcons.ExternalLink size={12} className="external-icon" />
                    </Link>
                  ) : (
                    <a href={sub.link} className="external-link" target="_blank" rel="noopener noreferrer">
                      {sub.title} <LucideIcons.ExternalLink size={12} className="external-icon" />
                    </a>
                  )
                ) : (
                  sub.title
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading journey data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="journey-container">
        <h1 className="journey-title">
          START YOUR <span className="journey" style={{color:"#c21717"}}>JOURNEY</span>
        </h1>

        <div className="d-flex justify-content-center gap-3 mb-4">
          <button
            className={`btn ${activeSection === 'faculty' ? 'btn-journey-active' : 'btn-journey'}`}
            onClick={() => setActiveSection('faculty')}
          >
            Faculty
          </button>
          <button
            className={`btn ${activeSection === 'student' ? 'btn-journey-active' : 'btn-journey'}`}
            onClick={() => setActiveSection('student')}
          >
            Student
          </button>
        </div>

        <div className="journey-menu">
          {activeSection === 'faculty' 
            ? renderMenuItems(facultyData) 
            : renderMenuItems(studentData)
          }
        </div>

        {activeSection === 'student' && (
          <p className="text-muted mt-4 small">
            * PhD Students can create their own public website, others can create internal sites only
          </p>
        )}
      </div>
    </div>
  );
};

export default JourneyView;